
import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useNotify } from "@/hooks/useNotify";
import { useAuth } from "@/contexts/AuthContext";
import { InvitationData, CompanyData, JoinStep } from "../types";
import { tabCoordinator } from "@/utils/tabCoordinator";

export const useInvitationData = () => {
  const [searchParams] = useSearchParams();
  const invitationId = searchParams.get("invitation");
  const superuserInvitationId = searchParams.get("superuser_invitation");
  const token = searchParams.get("token");
  const isSuperuserInvitation = !!superuserInvitationId;
  const effectiveInvitationId = superuserInvitationId || invitationId;
  const notify = useNotify();
  const { user, session, signOut } = useAuth(); // Remove isAuthReady dependency

  const [isLoading, setIsLoading] = useState(true);
  const [invitation, setInvitation] = useState<InvitationData | null>(null);
  const [company, setCompany] = useState<CompanyData | null>(null);
  const [currentStep, setCurrentStep] = useState<JoinStep>(JoinStep.INITIAL);
  const [currentUserEmail, setCurrentUserEmail] = useState<string | null>(null);
  const [invalidReason, setInvalidReason] = useState<string | null>(null);
  
  // Track if invitation data has been successfully loaded to prevent unnecessary re-fetching
  const invitationLoadedRef = useRef(false);
  
  useEffect(() => {
    const fetchInvitationDetails = async () => {
      if (!effectiveInvitationId) {
        notify.error("No invitation ID provided in the URL");
        setInvalidReason("No invitation ID provided in the URL");
        setIsLoading(false);
        return;
      }

      // Prevent re-fetching if data is already loaded and this is just an auth state change
      if (invitationLoadedRef.current && invitation && company) {
        console.log("Invitation data already loaded, skipping re-fetch");
        setIsLoading(false);
        return;
      }

      // Superuser invitations are handled exclusively by the admin portal
      if (isSuperuserInvitation) {
        console.log("Superuser invitation detected - redirecting user to admin portal");
        setInvalidReason("Superuser invitations must be accepted at admin.doyencegroup.com");
        setInvitation(null);
        setCompany(null);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);

        console.log("Fetching invitation with ID:", effectiveInvitationId);
        console.log("Token:", token);
        
        // Use secure RPC functions to validate invitation without exposing tokens
        // These functions only return data if the id+token combination is valid
        if (!token) {
          console.error("No token provided");
          setInvalidReason("Invalid invitation link - missing token");
          throw new Error("Invalid invitation link - missing token");
        }

        let invitationData = null;
        let invitationError = null;
        
        {
          // First try company_invitations (new system) via secure RPC
          const { data: companyInvitationData, error: companyInvitationError } = await supabase
            .rpc("get_invitation_by_token", {
              p_invitation_id: effectiveInvitationId,
              p_token: token
            })
            .maybeSingle();
          
          if (companyInvitationData) {
            // Transform RPC result to match expected shape
            invitationData = {
              id: companyInvitationData.id,
              company_id: companyInvitationData.company_id,
              email: companyInvitationData.email,
              status: companyInvitationData.status,
              expires_at: companyInvitationData.expires_at,
            };
          } else {
            // Fallback to legacy invitations table via secure RPC
            const { data: legacyInvitationData, error: legacyInvitationError } = await supabase
              .rpc("get_legacy_invitation_by_token", {
                p_invitation_id: effectiveInvitationId,
                p_token: token
              })
              .maybeSingle();
            
            if (legacyInvitationData) {
              invitationData = {
                id: legacyInvitationData.id,
                company_id: legacyInvitationData.company_id,
                email: legacyInvitationData.email,
                status: legacyInvitationData.status,
                expires_at: legacyInvitationData.expires_at,
              };
            }
            invitationError = legacyInvitationError;
          }
        }
        
        if (invitationError) {
          console.error("Error fetching invitation:", invitationError);
          setInvalidReason("Invalid or expired invitation");
          throw new Error("Invalid or expired invitation");
        }
        
        console.log("Invitation data retrieved via secure RPC:", invitationData ? "found" : "not found");

        // Add null check for invitationData - validation happens in the RPC function
        if (!invitationData) {
          console.error("No invitation data found - invalid token or expired");
          setInvalidReason("Invalid or expired invitation");
          throw new Error("Invalid or expired invitation");
        }

        // Check invitation status - allow "accepted" invitations if user email matches
        let statusCheckSession;
        try {
          const { data: { session } } = await supabase.auth.getSession();
          statusCheckSession = session;
        } catch (error) {
          console.error("Error getting session for status check:", error);
        }
        
        const currentUserEmail = statusCheckSession?.user?.email?.toLowerCase();
        const invitationEmail = invitationData.email.toLowerCase();
        
        if (invitationData.status !== "pending" && invitationData.status !== "accepted") {
          notify.info(`This invitation has been ${invitationData.status}`);
          setInvalidReason(`This invitation has been ${invitationData.status}`);
          setIsLoading(false);
          return;
        }
        
        // If invitation is accepted, only allow if current user email matches
        if (invitationData.status === "accepted" && currentUserEmail !== invitationEmail) {
          notify.error("This invitation has already been accepted by another user");
          setInvalidReason("This invitation has already been accepted by another user");
          setIsLoading(false);
          return;
        }
        
        // Log when we're allowing an accepted invitation to proceed
        if (invitationData.status === "accepted" && currentUserEmail === invitationEmail) {
          console.log("Allowing accepted invitation to proceed - user email matches:", invitationEmail);
        }
        
        let companyData: CompanyData | null = null;

        {
          const { data: fetchedCompany, error: companyError } = await supabase
            .rpc('get_company_info_by_invitation', {
              p_invitation_id: effectiveInvitationId,
              p_token: token,
            })
            .maybeSingle();

          if (companyError || !fetchedCompany) {
            console.error("Error fetching company details:", companyError);
            setInvalidReason("Invitation expired or invalid, or company not found");
            throw new Error("Could not retrieve company information");
          }
          companyData = fetchedCompany;
        }

        setInvitation(invitationData);
        setCompany(companyData);
        invitationLoadedRef.current = true;

        // Direct auth state determination - check current session
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        console.log("Current session check:", !!currentSession);
        
        if (currentSession?.user) {
          const currentUser = currentSession.user;
          setCurrentUserEmail(currentUser.email || null);
          
          if (currentUser.email?.toLowerCase() === invitationData.email.toLowerCase()) {
            console.log("User authenticated with matching email, skipping password creation");
            // For existing users, check if they need profile setup or can accept directly
            // This will be handled by the ExistingUserInvitationHandler component
            setCurrentStep(JoinStep.PROFILE_INFO);
          } else {
            console.log("[InvitationData] Email mismatch - expected:", invitationData.email, "got:", currentUser.email);
            
            // Enhanced cross-tab protection for sign-out operations
            const tabInfo = {
              tabId: tabCoordinator.getCurrentTabId(),
              shouldHandleAuth: tabCoordinator.shouldHandleAuth(),
              activeTabs: tabCoordinator.getActiveTabsInfo().length
            };
            
            console.log("[InvitationData] Preparing signout due to email mismatch", {
              ...tabInfo,
              expectedEmail: invitationData.email,
              actualEmail: currentUser.email
            });
            
            // Only proceed with sign-out if this tab should handle auth operations
            if (!tabCoordinator.shouldPreventSignOut('email_mismatch_in_join_flow')) {
              console.log("[InvitationData] Proceeding with signout - tab is authorized");
              notify.error("Please sign out and use the account associated with this invitation email.");
              
              // Use auth context signOut with reason to help with conflict detection
              await signOut(false, 'email_mismatch_in_join_flow');
              setCurrentStep(JoinStep.CREATE_PASSWORD);
            } else {
              console.log("[InvitationData] Preventing signout - cross-tab conflict detected", tabInfo);
              // Don't sign out, but show appropriate message and redirect
              notify.error("Close other tabs and sign in with the email associated with this invitation.");
              setCurrentStep(JoinStep.CREATE_PASSWORD);
            }
          }
        } else {
          console.log("No authenticated user, showing password creation");
          setCurrentStep(JoinStep.CREATE_PASSWORD);
        }

      } catch (error: any) {
        console.error("Error fetching invitation:", error);
        notify.error(error.message || "Could not find invitation");
        setInvalidReason(error.message || "Could not find invitation");
        setInvitation(null);
        setCompany(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInvitationDetails();
  }, [effectiveInvitationId, token, notify, isSuperuserInvitation]);

  // Separate effect to handle auth state changes after initial load
  useEffect(() => {
    if (invitation && !isLoading && user && session) {
      const userEmail = user.email?.toLowerCase();
      const invitationEmail = invitation.email.toLowerCase();
      
      if (userEmail === invitationEmail && currentStep === JoinStep.CREATE_PASSWORD) {
        console.log("Auth state updated after account creation - moving to profile step");
        setCurrentStep(JoinStep.PROFILE_INFO);
        setCurrentUserEmail(user.email || null);
      }
    }
  }, [user, session, invitation, isLoading, currentStep]);

  return {
    isLoading,
    invitation,
    company,
    currentStep,
    setCurrentStep,
    currentUserEmail,
    invitationId: effectiveInvitationId,
    token,
    invalidReason,
    isSuperuserInvitation
  };
};
